import { useState, useEffect, useMemo } from 'react'
import { adminApi } from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import { REFERRAL_SOURCES, type ReferralSource, type Registration, type SponsorshipSubmission, type AmbassadorSubmission } from '../../types'

interface ReferralStats {
  source: ReferralSource | 'Unknown'
  registrations: number
  sponsorships: number
  ambassadors: number
  total: number
}

type FormFilter = 'all' | 'golf' | 'sponsorship' | 'ambassador'

export default function Marketing() {
  const { getToken } = useAuth()
  const [registrations, setRegistrations] = useState<Registration[]>([])
  const [sponsorships, setSponsorships] = useState<SponsorshipSubmission[]>([])
  const [ambassadors, setAmbassadors] = useState<AmbassadorSubmission[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [formFilter, setFormFilter] = useState<FormFilter>('all')

  useEffect(() => {
    async function fetchData() {
      try {
        const token = await getToken()
        if (!token) throw new Error('Not authenticated')

        const [regsData, sponsorsData, ambassadorsData] = await Promise.all([
          adminApi.getRegistrations(token),
          adminApi.getSponsorships(token),
          adminApi.getAmbassadors(token),
        ])
        setRegistrations(regsData.registrations || [])
        setSponsorships(sponsorsData.sponsorships || [])
        setAmbassadors(ambassadorsData.ambassadors || [])
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load marketing data')
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [getToken])

  // Compute stats
  const stats = useMemo(() => {
    const sourceStats: Record<string, ReferralStats> = {}

    // Initialize all sources
    const allSources = [...REFERRAL_SOURCES, 'Unknown'] as const
    allSources.forEach(source => {
      sourceStats[source] = {
        source: source as ReferralSource | 'Unknown',
        registrations: 0,
        sponsorships: 0,
        ambassadors: 0,
        total: 0,
      }
    })

    // Count registrations (only paid ones for meaningful stats)
    registrations
      .filter(r => r.status === 'paid')
      .forEach(r => {
        const source = r.referralSource || 'Unknown'
        if (sourceStats[source]) {
          sourceStats[source].registrations++
          sourceStats[source].total++
        }
      })

    // Count sponsorships (paid or no_payment_required)
    sponsorships
      .filter(s => s.status === 'paid' || s.status === 'no_payment_required')
      .forEach(s => {
        const source = s.referralSource || 'Unknown'
        if (sourceStats[source]) {
          sourceStats[source].sponsorships++
          sourceStats[source].total++
        }
      })

    // Count ambassadors (paid ones)
    ambassadors
      .filter(a => a.status === 'paid')
      .forEach(a => {
        const source = a.referralSource || 'Unknown'
        if (sourceStats[source]) {
          sourceStats[source].ambassadors++
          sourceStats[source].total++
        }
      })

    return Object.values(sourceStats)
      .sort((a, b) => b.total - a.total)
  }, [registrations, sponsorships, ambassadors])

  // Filtered stats based on form type
  const filteredStats = useMemo(() => {
    return stats.map(s => {
      let count = 0
      switch (formFilter) {
        case 'golf':
          count = s.registrations
          break
        case 'sponsorship':
          count = s.sponsorships
          break
        case 'ambassador':
          count = s.ambassadors
          break
        default:
          count = s.total
      }
      return { ...s, displayCount: count }
    }).filter(s => s.displayCount > 0)
  }, [stats, formFilter])

  // Total counts
  const totals = useMemo(() => {
    return {
      registrations: registrations.filter(r => r.status === 'paid').length,
      sponsorships: sponsorships.filter(s => s.status === 'paid' || s.status === 'no_payment_required').length,
      ambassadors: ambassadors.filter(a => a.status === 'paid').length,
      total: stats.reduce((sum, s) => sum + s.total, 0),
      withReferral: stats.filter(s => s.source !== 'Unknown').reduce((sum, s) => sum + s.total, 0),
    }
  }, [registrations, sponsorships, ambassadors, stats])

  // Calculate percentages
  const getPercentage = (count: number, total: number) => {
    if (total === 0) return '0'
    return ((count / total) * 100).toFixed(1)
  }

  if (loading) {
    return (
      <div className="py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="card text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Marketing Analytics</h1>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card bg-blue-50 border border-blue-200">
          <p className="text-sm text-blue-600 font-medium">Total Responses</p>
          <p className="text-3xl font-bold text-blue-700">{totals.total}</p>
          <p className="text-xs text-blue-500 mt-1">With referral: {totals.withReferral}</p>
        </div>
        <div className="card bg-green-50 border border-green-200">
          <p className="text-sm text-green-600 font-medium">Golf Registrations</p>
          <p className="text-3xl font-bold text-green-700">{totals.registrations}</p>
        </div>
        <div className="card bg-purple-50 border border-purple-200">
          <p className="text-sm text-purple-600 font-medium">Sponsorships</p>
          <p className="text-3xl font-bold text-purple-700">{totals.sponsorships}</p>
        </div>
        <div className="card bg-orange-50 border border-orange-200">
          <p className="text-sm text-orange-600 font-medium">Ambassadors</p>
          <p className="text-3xl font-bold text-orange-700">{totals.ambassadors}</p>
        </div>
      </div>

      {/* Top Referral Source */}
      {filteredStats.length > 0 && (
        <div className="card bg-gradient-to-r from-sla-blue to-blue-600 text-white">
          <p className="text-sm opacity-80">Top Referral Source</p>
          <p className="text-2xl font-bold">{filteredStats[0].source}</p>
          <p className="text-sm opacity-80 mt-1">
            {filteredStats[0].displayCount} response{filteredStats[0].displayCount !== 1 ? 's' : ''} ({getPercentage(filteredStats[0].displayCount, filteredStats.reduce((sum, s) => sum + s.displayCount, 0))}%)
          </p>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2">
        <button
          onClick={() => setFormFilter('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            formFilter === 'all' ? 'bg-sla-blue text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All
        </button>
        <button
          onClick={() => setFormFilter('golf')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            formFilter === 'golf' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Golf
        </button>
        <button
          onClick={() => setFormFilter('sponsorship')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            formFilter === 'sponsorship' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Sponsorship
        </button>
        <button
          onClick={() => setFormFilter('ambassador')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            formFilter === 'ambassador' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Ambassador
        </button>
      </div>

      {/* Breakdown Table */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Referral Source Breakdown</h2>

        {filteredStats.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No referral data collected yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Source</th>
                  {formFilter === 'all' && (
                    <>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Golf</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Sponsor</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Ambassador</th>
                    </>
                  )}
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Count</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">%</th>
                  <th className="py-3 px-4 font-semibold text-gray-700">Distribution</th>
                </tr>
              </thead>
              <tbody>
                {filteredStats.map((stat) => {
                  const totalForPercentage = filteredStats.reduce((sum, s) => sum + s.displayCount, 0)
                  const percentage = parseFloat(getPercentage(stat.displayCount, totalForPercentage))
                  return (
                    <tr key={stat.source} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <span className="font-medium">{stat.source}</span>
                      </td>
                      {formFilter === 'all' && (
                        <>
                          <td className="text-right py-3 px-4 text-green-600">
                            {stat.registrations > 0 ? stat.registrations : '-'}
                          </td>
                          <td className="text-right py-3 px-4 text-purple-600">
                            {stat.sponsorships > 0 ? stat.sponsorships : '-'}
                          </td>
                          <td className="text-right py-3 px-4 text-orange-600">
                            {stat.ambassadors > 0 ? stat.ambassadors : '-'}
                          </td>
                        </>
                      )}
                      <td className="text-right py-3 px-4 font-semibold">{stat.displayCount}</td>
                      <td className="text-right py-3 px-4 text-gray-500">{percentage}%</td>
                      <td className="py-3 px-4">
                        <div className="w-full bg-gray-200 rounded-full h-4">
                          <div
                            className={`h-4 rounded-full ${
                              formFilter === 'golf' ? 'bg-green-500' :
                              formFilter === 'sponsorship' ? 'bg-purple-500' :
                              formFilter === 'ambassador' ? 'bg-orange-500' :
                              'bg-sla-blue'
                            }`}
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Notes */}
      <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4">
        <p className="font-medium mb-2">Notes:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Only paid/completed submissions are counted</li>
          <li>"Unknown" includes submissions before this feature was added</li>
          <li>Data updates in real-time as new submissions come in</li>
        </ul>
      </div>
    </div>
  )
}
